import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { AttributeOffsets, type Offset } from "../engine/attributeOffsets";
import type { IsoDateTime } from "../engine/dates";
import type { Bean, Grinder, Shot } from "../engine/model";
import {
  SEED_GRINDERS, SETTE_SEED, newestRoastFirst, newestShotFirst, normalizeBean, normalizeGrinder, normalizeShot,
  type AttributeOffsetsRepository, type BeanLearningRepository, type BeanRepository, type GrinderRepository,
  type ConfigVersion, type ConfigVersionRepository, type LearningEntry, type Repositories, type SettingsStore,
  type ShotRepository, type StoredNudge, type TasteNudgeRepository,
} from "./repositories";

/**
 * IndexedDB persistence mirroring the SQLite schema:
 *   grinders(id↑), beans(id↑), shots(id↑) with indexes bean_grinder_ts, bean_ts, grinder_ts, settings(key),
 *   attributeOffsets([attribute, value]), beanLearning([beanId, grinderId]).
 * Versions follow the SQLite history: v1 tables + seed grinders, v2 settings + grinder_ts, v3 offsets (seeded) +
 * learning ledger, v4 shots.taste (no store change; existing records read taste = null). Fields added later on existing
 * records are filled on read by the normalize* helpers. v5 dial format + recalibration (filled on read) and the
 * Baratza Sette seed, added only if no grinder of that name exists. v6 grinders.dialDirection (filled on read) and
 * two append-only stores: configVersions (tuned predictor parameters) and tasteNudges (index beanId).
 */
export const DB_NAME = "pucktech";
export const DB_VERSION = 6;

interface PuckTechDb extends DBSchema {
  grinders: { key: number; value: Grinder & { id: number } };
  beans: { key: number; value: Bean & { id: number } };
  shots: {
    key: number;
    value: Shot & { id: number };
    indexes: { bean_grinder_ts: [number, number, string]; bean_ts: [number, string]; grinder_ts: [number, string] };
  };
  settings: { key: string; value: { key: string; value: string } };
  attributeOffsets: { key: [string, string]; value: Offset };
  beanLearning: { key: [number, number]; value: LearningEntry };
  configVersions: { key: number; value: ConfigVersion & { id: number } };
  tasteNudges: { key: number; value: StoredNudge & { id: number }; indexes: { beanId: number } };
}

/** `version` exists so tests can build an older database and upgrade it; the app always opens at DB_VERSION. */
export async function openPuckTechDb(name = DB_NAME, version = DB_VERSION): Promise<IDBPDatabase<PuckTechDb>> {
  return openDB<PuckTechDb>(name, version, {
    upgrade(db, oldVersion, _newVersion, tx) {
      if (oldVersion < 1) {
        db.createObjectStore("grinders", { keyPath: "id", autoIncrement: true });
        db.createObjectStore("beans", { keyPath: "id", autoIncrement: true });
        const shots = db.createObjectStore("shots", { keyPath: "id", autoIncrement: true });
        shots.createIndex("bean_grinder_ts", ["beanId", "grinderId", "timestamp"]);
        shots.createIndex("bean_ts", ["beanId", "timestamp"]);
        const grinders = tx.objectStore("grinders");
        for (const g of SEED_GRINDERS) void grinders.add(g as Grinder & { id: number });
      }
      if (oldVersion < 2) {
        db.createObjectStore("settings", { keyPath: "key" });
        tx.objectStore("shots").createIndex("grinder_ts", ["grinderId", "timestamp"]);
      }
      if (oldVersion < 3) {
        const offsets = db.createObjectStore("attributeOffsets", { keyPath: ["attribute", "value"] });
        for (const o of AttributeOffsets.seeds().entries()) void offsets.add(o);
        db.createObjectStore("beanLearning", { keyPath: ["beanId", "grinderId"] });
      }
      if (oldVersion < 5 && version >= 5) {
        const grinders = tx.objectStore("grinders");
        void grinders.getAll().then((all) => {
          if (!all.some((g) => g.name === SETTE_SEED.name)) void grinders.add(SETTE_SEED as Grinder & { id: number });
        });
      }
      if (oldVersion < 6 && version >= 6) {
        db.createObjectStore("configVersions", { keyPath: "id", autoIncrement: true });
        db.createObjectStore("tasteNudges", { keyPath: "id", autoIncrement: true }).createIndex("beanId", "beanId");
      }
    },
  });
}

type Db = IDBPDatabase<PuckTechDb>;

export class IdbGrinderRepository implements GrinderRepository {
  constructor(private readonly db: Db) {}
  async save(g: Grinder): Promise<Grinder> {
    const n = normalizeGrinder(g);
    if (n.id === null) {
      const { id: _omit, ...rest } = n;
      const id = await this.db.add("grinders", rest as Grinder & { id: number });
      return { ...n, id };
    }
    await this.db.put("grinders", n as Grinder & { id: number });
    return n;
  }
  async findById(id: number): Promise<Grinder | null> {
    const g = await this.db.get("grinders", id);
    return g ? normalizeGrinder(g) : null;
  }
  async findAll(): Promise<Grinder[]> {
    return (await this.db.getAll("grinders")).map(normalizeGrinder).sort((a, b) => (a.id as number) - (b.id as number));
  }
}

export class IdbBeanRepository implements BeanRepository {
  constructor(private readonly db: Db) {}
  async save(b: Bean): Promise<Bean> {
    const n = normalizeBean(b);
    if (n.id === null) {
      const { id: _omit, ...rest } = n;
      const id = await this.db.add("beans", rest as Bean & { id: number });
      return { ...n, id };
    }
    await this.db.put("beans", n as Bean & { id: number });
    return n;
  }
  async findById(id: number): Promise<Bean | null> {
    const b = await this.db.get("beans", id);
    return b ? normalizeBean(b) : null;
  }
  async findActive(): Promise<Bean[]> {
    return (await this.db.getAll("beans")).map(normalizeBean).filter((b) => !b.archived).sort(newestRoastFirst);
  }
  async findAll(): Promise<Bean[]> { return (await this.db.getAll("beans")).map(normalizeBean).sort(newestRoastFirst); }
  async archive(id: number): Promise<void> {
    const b = await this.db.get("beans", id);
    if (b) await this.db.put("beans", { ...normalizeBean(b), archived: true } as Bean & { id: number });
  }
}

export class IdbShotRepository implements ShotRepository {
  constructor(private readonly db: Db) {}
  async save(s: Shot): Promise<Shot> {
    const n = normalizeShot(s);
    if (n.id === null) {
      const { id: _omit, ...rest } = n;
      const id = await this.db.add("shots", rest as Shot & { id: number });
      return { ...n, id };
    }
    await this.db.put("shots", n as Shot & { id: number });
    return n;
  }
  async findById(id: number): Promise<Shot | null> {
    const s = await this.db.get("shots", id);
    return s ? normalizeShot(s) : null;
  }
  async findByBeanAndGrinder(beanId: number, grinderId: number): Promise<Shot[]> {
    const range = IDBKeyRange.bound([beanId, grinderId, ""], [beanId, grinderId, "￿"]);
    return (await this.db.getAllFromIndex("shots", "bean_grinder_ts", range)).map(normalizeShot).sort(newestShotFirst);
  }
  async findByBean(beanId: number): Promise<Shot[]> {
    const range = IDBKeyRange.bound([beanId, ""], [beanId, "￿"]);
    return (await this.db.getAllFromIndex("shots", "bean_ts", range)).map(normalizeShot).sort(newestShotFirst);
  }
  async findLatestByGrinder(grinderId: number): Promise<Shot | null> {
    const range = IDBKeyRange.bound([grinderId, ""], [grinderId, "￿"]);
    const cursor = await this.db.transaction("shots").store.index("grinder_ts").openCursor(range, "prev");
    if (!cursor) return null;
    let best = cursor.value;
    let c: typeof cursor | null = cursor;
    while (c !== null && c.value.timestamp === best.timestamp) {
      if (c.value.id > best.id) best = c.value;
      c = await c.continue();
    }
    return normalizeShot(best);
  }
  async findAll(): Promise<Shot[]> { return (await this.db.getAll("shots")).map(normalizeShot).sort(newestShotFirst); }
  async delete(id: number): Promise<void> { await this.db.delete("shots", id); }
}

export class IdbSettingsStore implements SettingsStore {
  constructor(private readonly db: Db) {}
  async get(key: string): Promise<string | null> { return (await this.db.get("settings", key))?.value ?? null; }
  async put(key: string, value: string | null): Promise<void> {
    if (value === null) await this.db.delete("settings", key);
    else await this.db.put("settings", { key, value });
  }
  async entries(): Promise<Record<string, string>> {
    const all = await this.db.getAll("settings");
    return Object.fromEntries(all.map((e) => [e.key, e.value]));
  }
}

export class IdbAttributeOffsetsRepository implements AttributeOffsetsRepository {
  constructor(private readonly db: Db) {}
  async load(): Promise<AttributeOffsets> {
    const rows = await this.db.getAll("attributeOffsets");
    if (rows.length === 0) return AttributeOffsets.seeds();
    // keep the seed order so tables render consistently; unknown extra rows go last
    const order = new Map(AttributeOffsets.seeds().entries().map((o, i) => [`${o.attribute}:${o.value}`, i]));
    rows.sort((a, b) => (order.get(`${a.attribute}:${a.value}`) ?? 99) - (order.get(`${b.attribute}:${b.value}`) ?? 99));
    return AttributeOffsets.of(rows);
  }
  async save(offsets: AttributeOffsets): Promise<void> {
    const tx = this.db.transaction("attributeOffsets", "readwrite");
    await Promise.all([...offsets.entries().map((o) => tx.store.put(o)), tx.done]);
  }
  async resetToSeeds(): Promise<void> {
    const tx = this.db.transaction("attributeOffsets", "readwrite");
    await tx.store.clear();
    await Promise.all([...AttributeOffsets.seeds().entries().map((o) => tx.store.put(o)), tx.done]);
  }
}

export class IdbBeanLearningRepository implements BeanLearningRepository {
  constructor(private readonly db: Db) {}
  async isLearned(beanId: number, grinderId: number): Promise<boolean> {
    return (await this.db.get("beanLearning", [beanId, grinderId])) !== undefined;
  }
  async markLearned(beanId: number, grinderId: number, learnedAt: IsoDateTime): Promise<void> {
    await this.db.put("beanLearning", { beanId, grinderId, learnedAt });
  }
  async findAll(): Promise<LearningEntry[]> { return this.db.getAll("beanLearning"); }
  async clear(): Promise<void> { await this.db.clear("beanLearning"); }
}

export class IdbConfigVersionRepository implements ConfigVersionRepository {
  constructor(private readonly db: Db) {}
  async save(v: ConfigVersion): Promise<ConfigVersion> {
    if (v.id === null) {
      const { id: _drop, ...rest } = v;
      const id = await this.db.add("configVersions", rest as ConfigVersion & { id: number });
      return { ...v, id };
    }
    await this.db.put("configVersions", v as ConfigVersion & { id: number });
    return v;
  }
  async findAll(): Promise<ConfigVersion[]> { return this.db.getAll("configVersions"); }
}

export class IdbTasteNudgeRepository implements TasteNudgeRepository {
  constructor(private readonly db: Db) {}
  async save(n: StoredNudge): Promise<StoredNudge> {
    if (n.id === null) {
      const { id: _drop, ...rest } = n;
      const id = await this.db.add("tasteNudges", rest as StoredNudge & { id: number });
      return { ...n, id };
    }
    await this.db.put("tasteNudges", n as StoredNudge & { id: number });
    return n;
  }
  async findByBean(beanId: number): Promise<StoredNudge[]> {
    return (await this.db.getAllFromIndex("tasteNudges", "beanId", beanId)).sort((a, b) => a.id - b.id);
  }
  async findAll(): Promise<StoredNudge[]> { return this.db.getAll("tasteNudges"); }
}

export class IdbRepositories implements Repositories {
  readonly grinders: IdbGrinderRepository;
  readonly beans: IdbBeanRepository;
  readonly shots: IdbShotRepository;
  readonly settings: IdbSettingsStore;
  readonly offsets: IdbAttributeOffsetsRepository;
  readonly learning: IdbBeanLearningRepository;
  readonly configVersions: IdbConfigVersionRepository;
  readonly nudges: IdbTasteNudgeRepository;

  private constructor(private readonly db: Db) {
    this.grinders = new IdbGrinderRepository(db);
    this.beans = new IdbBeanRepository(db);
    this.shots = new IdbShotRepository(db);
    this.settings = new IdbSettingsStore(db);
    this.offsets = new IdbAttributeOffsetsRepository(db);
    this.learning = new IdbBeanLearningRepository(db);
    this.configVersions = new IdbConfigVersionRepository(db);
    this.nudges = new IdbTasteNudgeRepository(db);
  }

  static async open(name = DB_NAME): Promise<IdbRepositories> {
    return new IdbRepositories(await openPuckTechDb(name));
  }

  async clearAll(): Promise<void> {
    const stores = ["grinders", "beans", "shots", "settings", "beanLearning", "configVersions", "tasteNudges"] as const;
    const tx = this.db.transaction([...stores], "readwrite");
    await Promise.all([...stores.map((s) => tx.objectStore(s).clear()), tx.done]);
    await this.offsets.resetToSeeds();
  }

  close(): void { this.db.close(); }
}
